// ─── FieldChangeMarker — inline "someone else changed this" dot ─────────────
// Renders next to an editable field. If the most recent audit row for this
// fieldKey was made by ANOTHER user within the last 48h, shows a colored dot
// with a hover tooltip: "Jane · 950 → 1,100 · 2h ago". Otherwise renders null.
//
// The dot is a dismissible hint — click the × to dismiss for this field key
// (persisted to localStorage). Dismissals are local; the central Activity Log
// always retains the full history.
//
// Usage:
//   <FieldChangeMarker fieldKey={makeFieldKey("month", "2026-04", "target")} />
//
// Keep this component cheap — it mounts once per editable field, so ~80
// instances per page. No network calls (reads from useRecentChanges cache).

import { useState, useMemo } from "react";
import { useTheme } from "../lib/ThemeContext.jsx";
import { useRecentChanges } from "../hooks/useRecentChanges.js";
import { supabase } from "../hooks/useData.js";

const STALE_MS = 48 * 60 * 60 * 1000; // 48h — after this the marker hides

function formatAgo(iso) {
  const d = new Date(iso);
  const ms = Date.now() - d.getTime();
  if (ms < 60_000) return "just now";
  if (ms < 3_600_000) return `${Math.round(ms / 60_000)}m ago`;
  if (ms < 86_400_000) return `${Math.round(ms / 3_600_000)}h ago`;
  return `${Math.round(ms / 86_400_000)}d ago`;
}

// Best-effort one-liner for any audit value. Mirrors the array-of-objects
// handling in Profile.jsx so we never render the raw "[object Object]"
// fallback in the field-change tooltips either.
function fmtVal(v) {
  if (v == null || v === "") return "∅";
  if (Array.isArray(v)) {
    if (v.length === 0) return "none";
    const names = v.map(x => x == null ? null
      : (typeof x === "object" ? (x.name || x.what || x.label || x.title) : String(x))).filter(Boolean);
    if (names.length === v.length && v.length <= 3) return names.join(", ");
    return `${v.length} items`;
  }
  if (typeof v === "object") {
    const label = v.name || v.what || v.label || v.title;
    if (label) return String(label);
    try { return JSON.stringify(v).slice(0, 30); } catch { return "[object]"; }
  }
  const s = String(v);
  return s.length > 30 ? s.slice(0, 27) + "…" : s;
}

export default function FieldChangeMarker({ fieldKey, compact = false }) {
  const T = useTheme();
  const { byFieldKey } = useRecentChanges();
  const [dismissed, setDismissed] = useState(() => {
    try { return localStorage.getItem(`sf_fmark_${fieldKey}`) || ""; } catch { return ""; }
  });
  const [hovering, setHovering] = useState(false);

  // Current-user email is cached by supabase — cheap sync access.
  const myEmail = useMemo(() => {
    try {
      const raw = localStorage.getItem("sb-auth-user-email");
      if (raw) return raw;
    } catch {}
    const s = supabase.auth.getSession?.();
    return s?.data?.session?.user?.email || null;
  }, []);

  const row = byFieldKey.get(fieldKey);
  if (!row) return null;
  if (Date.now() - new Date(row.created_at).getTime() > STALE_MS) return null;
  if (row.email && myEmail && row.email === myEmail) return null;
  if (dismissed && dismissed === row.id) return null;

  function dismiss(e) {
    e.stopPropagation();
    try { localStorage.setItem(`sf_fmark_${fieldKey}`, row.id); } catch {}
    setDismissed(row.id);
  }

  const d = row.details || {};
  const hasDiff = d.from !== undefined || d.to !== undefined;
  const actor = (row.email || "teammate").split("@")[0];

  const size = compact ? 6 : 7;
  return (
    <span
      onMouseEnter={() => setHovering(true)}
      onMouseLeave={() => setHovering(false)}
      style={{ position:"relative", display:"inline-flex", alignItems:"center", marginLeft:4, verticalAlign:"middle" }}
      aria-label={`Changed by ${actor}`}
    >
      <span style={{
        width:size, height:size, borderRadius:"50%",
        background:T.accent, boxShadow:`0 0 0 2px ${T.accent}30`,
        display:"inline-block", cursor:"help",
      }}/>
      {hovering && (
        <span style={{
          position:"absolute", bottom:"calc(100% + 6px)", left:"50%", transform:"translateX(-50%)",
          background:T.card, border:`1px solid ${T.border}`, borderRadius:6,
          padding:"6px 9px", fontSize:11, color:T.text, whiteSpace:"nowrap",
          boxShadow:"0 4px 14px rgba(0,0,0,0.25)", zIndex:10000, lineHeight:1.4,
          fontFamily:"inherit", pointerEvents:"auto",
        }}>
          <div><strong>{actor}</strong>{hasDiff ? "" : " made a change"}</div>
          {hasDiff && (
            <div style={{ color:T.textSub, fontVariantNumeric:"tabular-nums" }}>
              <span style={{ color:T.red }}>{fmtVal(d.from)}</span>
              {" → "}
              <span style={{ color:T.green }}>{fmtVal(d.to)}</span>
            </div>
          )}
          <div style={{ color:T.textMuted, marginTop:2 }}>{formatAgo(row.created_at)}</div>
          <button onClick={dismiss} style={{
            marginTop:4, background:"transparent", border:"none",
            color:T.textMuted, fontSize:10, cursor:"pointer", padding:0,
            textDecoration:"underline",
          }}>Dismiss</button>
        </span>
      )}
    </span>
  );
}
